/* global axios, window, document, FormData, setTimeout, clearTimeout */
/* eslint react/no-unknown-property: ["error", { "ignore": ["condition"] }] */
import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import TextInput from "./TextInput";
import TextAreaInput from "./TextAreaInput";
import SelectInput from "./SelectInput";
import ImageMessage from "./ImageMessage";
import NextButton from "./NextButton";
import EditButton from "./EditButton";
import EditInput from "./EditInput";
import TitleTab from "./TitleTab";
import Switch from "./Switch";
import PreviewCover from "./PreviewCover";
import Categories from "./Categories";
import Confetti from "../Confetti";
import StepLayout from "./StepLayout";
import AddWebsite from "./AddWebsite";
import Transcripts from "./Transcripts";
import SkipOrNext from "./SkipOrNext";
import QuickActions from "./QuickActions";
import SelectWebsiteType from "./SelectWebsiteType";
import Heroicon from "../Heroicon";

function CreatePodcast({
  coverUrl,
  firstPodcast,
  fromWizard,
  transcriptsLimit,
}) {
  const Castos = window.Castos;
  let [podcastData, setPodcastData] = useState({
    podcast_title: "",
    podcast_description: "",
    website: "",
    author_name: Castos.config.userName,
    podcast_owner: Castos.config.userName,
    owner_email: Castos.config.userEmail,
    copyright: "© " + new Date().getFullYear(),
    language: "en",
    feed_episodes_number: 100,
    cover_image: "",
    podcast_slug: null,
    explicit: 0,
    transcripts_enabled: 1,
    chapters_enabled: 1,
  });

  const defaultCovers = Object.entries(Castos.config.podcastImage).map(
    (cover) => ({
      key: cover[0],
      value: cover[1],
    })
  );

  // Index of current page. To show/hide each step in the form
  let [activeIndex, setActiveIndex] = useState(1);

  // State to display podcast cover in front-end and validate dimensions and aspect ratio
  let [podcastCover, setPodcastCover] = useState({
    src: "",
    size: null,
    dimensions: null,
    validType: null,
    validSize: null,
    validRatio: null,
    validDimensions: null,
    validDimensionsMsg: null,
  });
  // Possible values: null, error, available, unavailable
  let [slugValidation, setSlugValidation] = useState(null);

  // To enable/disable editing Review Details section
  let [editSection, setEditSection] = useState(null);

  // To verify if the create podcast request failed or succeeded
  let [requestState, setRequestState] = useState(null);

  let [languages, setLanguages] = useState([]);
  let [categoriesList, setCategoriesList] = useState([]);

  let [websiteType, setWebsiteType] = useState("castos");
  let [validWebsite, setValidWebsite] = useState(false);

  function handleChange(name, value) {
    setPodcastData({
      ...podcastData,
      [name]: value,
    });
  }

  function createPodcast(e) {
    e.preventDefault();
    setRequestState("loading");

    const formData = new FormData();
    Object.keys(podcastData).forEach((key) => {
      if (key === "website" && websiteType === "castos") {
        formData.append(
          key,
          podcastData.podcast_slug + "." + Castos.config.subRouting
        );
      } else {
        formData.append(key, podcastData[key]);
      }
    });

    if (websiteType === "custom") {
      formData.append("private_podcast", 1);
    }

    axios
      .post("/api/v2/podcasts", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(function (response) {
        if (response.data.success) {
          // Show "You're all set page"
          setActiveIndex(7);
          setRequestState("success");
          handleChange("podcast_id", response.data.data.podcast.id);
        } else {
          setRequestState(["Something went wrong, please try again."]);
          // Scroll to top to see error messages
          window.scrollTo({
            top: 0,
          });
        }
      })
      .catch(function (error) {
        if (error.response?.data?.errors) {
          const errorArr = Object.values(error.response.data.errors);
          setRequestState(errorArr);
        } else {
          setRequestState(["Something went wrong, please try again."]);
        }
        // Scroll to top to see error messages
        window.scrollTo({
          top: 0,
        });
      });
  }

  function togglePage(e, page) {
    e.preventDefault();
    setActiveIndex(page);
  }

  function toggleEdit(e, section) {
    e.preventDefault();
    setEditSection(section);
  }

  // In case user doesn't enter a podcast title or description
  function fillOverviewData() {
    const podcastTitle = podcastData.podcast_title
      ? podcastData.podcast_title
      : Castos.config.userName + "'s Podcast";

    setPodcastData({
      ...podcastData,
      podcast_title: podcastTitle,
      podcast_description: podcastData.podcast_description
        ? podcastData.podcast_description
        : "Welcome to " +
          Castos.config.userName +
          "'s podcast, powered by Castos",
    });
  }

  function removeImage(e) {
    e.preventDefault();
    handleChange("cover_image", "");
    setPodcastCover({
      src: "",
      size: null,
      validType: null,
      validSize: null,
      dimensions: null,
      validRatio: null,
      validDimensions: null,
      validDimensionsMsg: null,
    });
  }

  // API call to allow user check for the slug's availability
  function checkUniqueSlug() {
    let generatedSlug = podcastData.podcast_slug;

    axios
      .post("/api/v2/podcast/validate-slug", {
        podcast_slug: generatedSlug,
      })
      .then(function (response) {
        if (generatedSlug === null) {
          setSlugValidation(null);
        } else {
          setSlugValidation(
            response.data.success ? "available" : "unavailable"
          );
        }
      })
      .catch(function () {});
  }

  // Check slug's availability every time user changes the podcast_slug input
  useEffect(() => {
    if (podcastData.podcast_slug !== "") {
      setSlugValidation(null);
      let timer = setTimeout(() => {
        checkUniqueSlug();
      }, 500);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [podcastData.podcast_slug]);

  // API call to get languages list
  useEffect(() => {
    axios
      .get("/api/v2/get-languages/")
      .then(function (response) {
        const responseInfo = response.data.languages;

        const allLanguages = Object.entries(responseInfo).map((language) => ({
          key: language[0],
          value: language[1],
        }));

        setLanguages(allLanguages);
      })
      .catch(function () {});
  }, []);

  useEffect(() => {
    fromWizard
      ? window.applyTags("create_podcast_welcome")
      : window.applyTags("create_podcast_dashboard");

    axios
      .get("/api/v2/get-categories/")
      .then(function (response) {
        setCategoriesList(response.data.categories);
      })
      .catch(function () {});
  }, []);

  useEffect(() => {
    if (podcastData.transcripts_enabled === 0) {
      handleChange("chapters_enabled", 0);
    }
  }, [podcastData.transcripts_enabled]);

  function sanitizeString(str) {
    const newStr = str
      // Removes sequences of emojis
      .replace(
        /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
        ""
      )
      .replace(/\s+/g, " ")
      // Prevents string from starting with a whitespace
      .replace(/^\s+/g, "");

    return newStr;
  }

  function getDefaultCoverSrc(key) {
    const src = defaultCovers.filter((cover) => {
      return cover.key === key;
    });
    return src[0].value;
  }

  function selectDefaultCover(e) {
    e.preventDefault();
    handleChange("default_cover_image", e.target.dataset.value);
  }

  function canContinueFromWebsite() {
    if (websiteType === "castos") {
      return slugValidation === "available" && podcastData.podcast_slug !== "";
    } else {
      return podcastData.website !== "" && validWebsite;
    }
  }

  function handleWebsiteNext(callback) {
    // Check if slug ends with a dash
    if (/-+$/.test(podcastData.podcast_slug)) {
      setSlugValidation("error");
    } else {
      callback();
    }
  }

  return (
    <>
      <header className="mt-6 md:mt-0">
        {activeIndex < 6 && (
          <>
            <div className="flex md:justify-center">
              <a
                href="/imports/submit"
                className="inline-block mb-4 text-sm badge badge--secondary"
              >
                Are you looking to import an existing podcast? Go to Imports{" "}
                <Heroicon icon="o-arrow-right" style="w-3 h-3 inline-block" />
              </a>
            </div>
            <h1 className="text-2xl text-gray-800 md:text-3xl md:text-center">
              Tell us more about your podcast
            </h1>
            <p className="mt-4 text-gray-600 md:text-center">
              We collect the necessary information required by podcast
              directories for a valid RSS feed. You can always edit your podcast
              settings later.
            </p>
          </>
        )}
        {activeIndex === 6 && (
          <>
            <h1 className="text-2xl text-gray-800 md:text-3xl md:text-center">
              Review podcast details
            </h1>
            <p className="mt-4 text-gray-600 md:text-center">
              Here&apos;s a quick recap of the information we&apos;ll use to
              create your podcast.
            </p>
            {Array.isArray(requestState) ? (
              <ul className="items-center gap-2 mt-4 text-sm alert alert--danger md:mx-auto w-max">
                <Heroicon icon="o-exclamation-circle" style="w-6 h-6" />
                <div>
                  {requestState.map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </div>
              </ul>
            ) : (
              <p className="flex items-center gap-2 mt-4 text-xs text-blue-700 badge badge--info sm:text-sm md:mx-auto w-max">
                <Heroicon icon="o-information-circle" style="w-5 h-5" />
                You can always edit your podcast settings later
              </p>
            )}
          </>
        )}
        {activeIndex === 7 && (
          <>
            <h1 className="text-2xl text-gray-800 md:text-3xl md:text-center">
              You&apos;re all set 🎉
            </h1>
            <p className="mt-4 text-gray-600 md:text-center">
              Well done, your podcast is ready.
            </p>
            <p className="mt-2 text-gray-600 md:text-center">
              Let&apos;s keep going by adding your first episode!
            </p>
          </>
        )}
      </header>

      {activeIndex < 7 && (
        <div className="grid-cols-6 gap-2 mt-8 md:grid">
          <TitleTab title="Overview" index={activeIndex} indexNum={1} />
          <TitleTab title="Cover" index={activeIndex} indexNum={2} />
          <TitleTab title="Website" index={activeIndex} indexNum={3} />
          <TitleTab title="Categories" index={activeIndex} indexNum={4} />
          <TitleTab title="AI Assistant" index={activeIndex} indexNum={5} />
          <TitleTab title="Summary" index={activeIndex} indexNum={6} />
        </div>
      )}

      {activeIndex < 7 && (
        <form
          method="POST"
          id="create-podcast"
          className={`bg-white ${
            activeIndex <= 7 ? "rounded-t-none" : "mt-6"
          } md:mt-6 rounded-b-lg md:rounded-lg shadow mb-20 relative overflow-hidden`}
          onSubmit={(e) => e.preventDefault()}
        >
          {activeIndex === 1 && (
            <StepLayout
              title="Podcast Information"
              description="Tell us a bit about your podcast by giving it a title and short description."
            >
              <TextInput
                value={podcastData.podcast_title}
                name={"podcast_title"}
                title={"Title"}
                showLabel={true}
                tooltip="Give your podcast a title that will help listeners find your show."
                handleChange={(e) =>
                  handleChange("podcast_title", sanitizeString(e.target.value))
                }
              />

              <TextAreaInput
                value={podcastData.podcast_description}
                showLabel={true}
                tooltip="A few sentences that describe your show and who it's for."
                handleChange={(e) =>
                  handleChange("podcast_description", e.target.value)
                }
              />

              <SkipOrNext
                handleClick={(e, type) => {
                  togglePage(e, 2);
                  fillOverviewData();
                  if (firstPodcast) {
                    window.applyTags("create_first_podcast_title_description", {
                      skipped: type === "skip" ? "yes" : "no",
                      hasTitle: podcastData.podcast_title !== "" ? "yes" : "no",
                      hasDescription:
                        podcastData.podcast_description !== "" ? "yes" : "no",
                    });
                  }
                }}
              />
            </StepLayout>
          )}

          {activeIndex === 2 && (
            <StepLayout
              title="Cover Image"
              description="A cover image is essential for attracting attention, building your brand, and ensuring your podcast is presented professionally across various platforms and marketing channels."
            >
              <div className="grid sm:grid-cols-[auto,1fr] gap-1 sm:gap-6">
                <PreviewCover
                  handleChange={handleChange}
                  podcastCover={podcastCover}
                  setPodcastCover={setPodcastCover}
                />

                <div className="sm:mt-3">
                  <h2 className="hidden text-xl text-gray-800 md:text-2xl md:block">
                    Podcast Cover
                  </h2>
                  <ImageMessage podcastCover={podcastCover} />
                </div>
              </div>

              <div className="flex flex-col items-center justify-between gap-3 mt-6 sm:flex-row">
                {podcastCover.src === "" ? (
                  <p className="w-full text-sm bg-yellow-100 rounded-lg px-3 py-3 text-yellow-700 grid grid-cols-[auto,1fr,auto] gap-2 items-center font-medium">
                    <Heroicon icon="o-sparkles" style="w-5 h-5" />
                    Create a custom cover with our free tool
                    <a
                      href={`https://dynamo.castos.com/podcast-covers?t=${podcastData.podcast_title}&s=With%20${podcastData.author_name}&utm_source=castos&utm_medium=onboarding&utm_campaign=step-2-cover`}
                      rel="noreferrer"
                      target="_blank"
                      className="bg-yellow-300 px-2.5 py-1.5 rounded-md inline-flex items-center hover:bg-yellow-400 hover:text-yellow-900"
                    >
                      Dynamo{" "}
                      <Heroicon icon="o-arrow-up-right" style="w-4 h-4 ml-1" />
                    </a>
                  </p>
                ) : (
                  <button
                    className="flex items-center justify-center w-full py-2 mb-3 text-base rounded-lg btn-solid btn-secondary px-7 sm:px-8 sm:py-3 sm:w-56 sm:mb-0"
                    onClick={(e) => removeImage(e)}
                  >
                    <Heroicon
                      icon="o-trash"
                      style="w-5 h-5 mr-2 text-gray-400"
                    />
                    Remove image
                  </button>
                )}
              </div>
              <SkipOrNext
                conditionForNext={
                  podcastCover.src === "" ||
                  (podcastCover.validDimensions && podcastCover.validRatio)
                }
                handleClick={(e, type) => {
                  togglePage(e, 3);
                  if (type === "skip" && podcastCover.src !== "") {
                    removeImage(e);
                  }
                  if (firstPodcast) {
                    window.applyTags("create_first_podcast_image", {
                      skipped: type === "skip" ? "yes" : "no",
                      hasImage: podcastCover.src !== "" ? "yes" : "no",
                    });
                  }
                }}
              />
            </StepLayout>
          )}

          {activeIndex === 3 && (
            <StepLayout
              title="Podcast Website"
              description="Your podcast website is a great way to share more about you and your show with listeners. These include things like host profiles, show notes, transcripts, and links to your show on social media."
            >
              <SelectWebsiteType
                websiteType={websiteType}
                setWebsiteType={setWebsiteType}
                podcastData={podcastData}
                handleChange={handleChange}
              />

              <AddWebsite
                websiteType={websiteType}
                podcastData={podcastData}
                handleChange={handleChange}
                slugValidation={slugValidation}
                validWebsite={validWebsite}
                setValidWebsite={setValidWebsite}
              />

              <div className="flex justify-end mt-6">
                <NextButton
                  condition={canContinueFromWebsite()}
                  handleChange={(e) => {
                    handleWebsiteNext(() => {
                      togglePage(e, 4);
                      if (firstPodcast) {
                        window.applyTags("create_first_podcast_website", {
                          usesCastosWebsite:
                            websiteType === "castos" ? "yes" : "no",
                          usesCustomWebsite:
                            websiteType === "custom" ? "yes" : "no",
                        });
                      }
                    });
                  }}
                />
              </div>
            </StepLayout>
          )}

          {activeIndex === 4 && (
            <section>
              <StepLayout
                title="Podcast Categories"
                description="Choose at least one category below. This is where your podcast will appear in directories like Apple Podcasts, and is a great way for new listeners to find your show."
              >
                <Categories
                  podcastData={podcastData}
                  handleChange={handleChange}
                  categoriesList={categoriesList}
                />
                <SkipOrNext
                  handleClick={(e, type) => {
                    togglePage(e, 5);
                    if (firstPodcast) {
                      window.applyTags("create_first_podcast_category", {
                        skipped: type === "skip" ? "yes" : "no",
                        hasPrimaryCategory: podcastData.itunes_category1
                          ? "yes"
                          : "no",
                        hasSecondaryCategory: podcastData.itunes_category2
                          ? "yes"
                          : "no",
                        hasTertiaryCategory: podcastData.itunes_category3
                          ? "yes"
                          : "no",
                      });
                    }
                  }}
                />
              </StepLayout>
            </section>
          )}

          {activeIndex === 5 && (
            <section>
              <StepLayout
                title="AI Assistant"
                description={`Extend the reach, searchability, and accessibility of your podcast with AI-powered features for each episode. ${
                  transcriptsLimit > 0
                    ? transcriptsLimit +
                      " episode transcripts are included in your plan at no cost."
                    : ""
                }`}
              >
                <Transcripts
                  podcastData={podcastData}
                  handleChange={handleChange}
                />
                <div className="flex justify-end mt-6">
                  <NextButton
                    handleChange={(e) => {
                      togglePage(e, 6);
                      if (firstPodcast) {
                        window.applyTags("create_first_podcast_transcript", {
                          generateTranscripts:
                            podcastData.transcripts_enabled === 1
                              ? "yes"
                              : "no",
                        });
                      }
                    }}
                  />
                </div>
              </StepLayout>
            </section>
          )}

          {activeIndex === 6 && (
            <>
              <section
                className={`pb-4 border-b border-gray-200 px-7 py-5 transition-opacity ${
                  editSection !== null &&
                  editSection !== "overview" &&
                  "opacity-50"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-gray-800 md:text-2xl">
                    01. Podcast overview
                  </h2>

                  {editSection !== "overview" && (
                    <EditButton
                      condition={editSection === null}
                      type="Edit"
                      handleChange={(e) => toggleEdit(e, "overview")}
                    />
                  )}

                  {editSection === "overview" && (
                    <EditButton
                      condition={
                        editSection === "overview" &&
                        podcastData.podcast_title &&
                        podcastData.podcast_description
                      }
                      type="Update"
                      handleChange={(e) => toggleEdit(e, null)}
                    />
                  )}
                </div>

                <EditInput title="Title">
                  <Switch test={editSection === "overview" ? 1 : 0}>
                    <TextInput
                      condition={1}
                      value={podcastData.podcast_title}
                      name={"podcast_title"}
                      title={"Title"}
                      handleChange={(e) =>
                        handleChange(
                          "podcast_title",
                          sanitizeString(e.target.value)
                        )
                      }
                      required={true}
                    />
                    <p condition={0} className="text-gray-800 sm:px-3 py-[9px]">
                      {podcastData.podcast_title}
                    </p>
                  </Switch>
                </EditInput>

                <EditInput title="Description">
                  <Switch test={editSection === "overview" ? 1 : 0}>
                    <TextAreaInput
                      condition={1}
                      value={podcastData.podcast_description}
                      required={true}
                      handleChange={(e) =>
                        handleChange("podcast_description", e.target.value)
                      }
                    />
                    <p condition={0} className="text-gray-800 sm:px-3 py-[9px]">
                      {podcastData.podcast_description}
                    </p>
                  </Switch>
                </EditInput>
              </section>
              <section
                className={`border-b border-gray-200 px-7 py-5 transition-opacity ${
                  editSection !== null &&
                  editSection !== "cover" &&
                  "opacity-50"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-gray-800 md:text-2xl">
                    02. Cover Artwork
                  </h2>

                  {editSection !== "cover" && (
                    <EditButton
                      condition={editSection === null}
                      type="Edit"
                      handleChange={(e) => toggleEdit(e, "cover")}
                    />
                  )}

                  {editSection === "cover" && (
                    <EditButton
                      condition={
                        podcastCover.src === "" ||
                        (podcastCover.validDimensions &&
                          podcastCover.validRatio)
                      }
                      type="Update"
                      handleChange={(e) => toggleEdit(e, null)}
                    />
                  )}
                </div>

                <Switch test={editSection === "cover" ? 1 : 0}>
                  <div condition={1} className="gap-6 sm:flex">
                    <PreviewCover
                      handleChange={handleChange}
                      podcastCover={podcastCover}
                      setPodcastCover={setPodcastCover}
                    />

                    <div className="flex-1">
                      <ImageMessage podcastCover={podcastCover} />

                      {podcastCover.src !== "" && (
                        <button
                          className="flex items-center justify-center w-full mt-4 rounded-lg btn-solid btn-secondary sm:w-44"
                          onClick={(e) => removeImage(e)}
                        >
                          <Heroicon
                            icon="o-trash"
                            style="w-5 h-5 mr-2 text-gray-400"
                          />
                          Remove image
                        </button>
                      )}
                    </div>
                  </div>
                  <div condition={0}>
                    {podcastCover.src === "" ? (
                      <>
                        <p className="mt-2 mb-4 text-gray-500">
                          Select a pre-designed cover, create one with{" "}
                          <a
                            href={`https://dynamo.castos.com/podcast-covers?t=${podcastData.podcast_title}&s=With%20${podcastData.author_name}&utm_source=castos&utm_medium=onboarding&utm_campaign=step-5-summary`}
                            target="_blank"
                            className="underline"
                            rel="noreferrer"
                          >
                            Dynamo
                          </a>{" "}
                          or edit to upload your own.
                        </p>
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                          {editSection !== null && editSection !== "cover" ? (
                            <>
                              {defaultCovers.map((cover) => (
                                <button
                                  disabled
                                  key={cover.key}
                                  data-value={cover.key}
                                  onClick={selectDefaultCover}
                                  className={`${
                                    podcastData.default_cover_image ===
                                    cover.key
                                      ? "ring-4 ring-purple-300 ring-offset-4 ring-offset-white"
                                      : ""
                                  } rounded-lg cursor-default`}
                                >
                                  <img
                                    data-value={cover.key}
                                    className="rounded-lg"
                                    src={coverUrl + cover.value}
                                    alt={cover.key}
                                  />
                                </button>
                              ))}
                            </>
                          ) : (
                            <>
                              {defaultCovers.map((cover) => (
                                <button
                                  key={cover.key}
                                  data-value={cover.key}
                                  onClick={selectDefaultCover}
                                  className={`${
                                    podcastData.default_cover_image ===
                                    cover.key
                                      ? "ring-4 ring-purple-300 ring-offset-4 ring-offset-white"
                                      : ""
                                  } rounded-lg`}
                                >
                                  <img
                                    data-value={cover.key}
                                    className="rounded-lg"
                                    src={coverUrl + cover.value}
                                    alt={cover.key}
                                  />
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="overflow-hidden rounded-xl h-0 pb-[100%] sm:pb-0 sm:h-56 sm:w-56 relative">
                        <img
                          src={podcastCover.src}
                          className="absolute inset-0 block object-cover min-h-full rounded-xl"
                          alt="Podcast Cover"
                        />
                      </div>
                    )}
                  </div>
                </Switch>
              </section>

              <section
                className={`border-b border-gray-200 px-7 py-5 transition-opacity ${
                  editSection !== null &&
                  editSection !== "categories" &&
                  "opacity-50"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-gray-800 md:text-2xl">
                    03. Categories
                  </h2>

                  {editSection !== "categories" && (
                    <EditButton
                      condition={editSection === null}
                      type="Edit"
                      handleChange={(e) => toggleEdit(e, "categories")}
                    />
                  )}

                  {editSection === "categories" && (
                    <EditButton
                      condition={editSection === "categories"}
                      type="Update"
                      handleChange={(e) => toggleEdit(e, null)}
                    />
                  )}
                </div>

                <Switch test={editSection === "categories" ? 1 : 0}>
                  <div condition={0}>
                    <EditInput title="Primary">
                      <p className="text-gray-800 sm:px-3 py-[9px]">
                        {!podcastData.itunes_category1
                          ? "Not set"
                          : podcastData.itunes_category1}
                      </p>
                    </EditInput>
                    <EditInput title="Secondary">
                      <p className="text-gray-800 sm:px-3 py-[9px]">
                        {!podcastData.itunes_category2
                          ? "Not set"
                          : podcastData.itunes_category2}
                      </p>
                    </EditInput>
                    <EditInput title="Tertiary">
                      <p className="text-gray-800 sm:px-3 py-[9px]">
                        {!podcastData.itunes_category3
                          ? "Not set"
                          : podcastData.itunes_category3}
                      </p>
                    </EditInput>
                  </div>
                  <Categories
                    condition={1}
                    podcastData={podcastData}
                    handleChange={handleChange}
                    categoriesList={categoriesList}
                  />
                </Switch>
              </section>

              <section
                className={`border-b border-gray-200 px-7 py-5 transition-opacity ${
                  editSection !== null && editSection !== "slug" && "opacity-50"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-gray-800 md:text-2xl">
                    04. Website
                  </h2>

                  {editSection !== "slug" && (
                    <EditButton
                      condition={editSection === null}
                      type="Edit"
                      handleChange={(e) => toggleEdit(e, "slug")}
                    />
                  )}

                  {editSection === "slug" && (
                    <EditButton
                      condition={
                        editSection === "slug" && canContinueFromWebsite()
                      }
                      type="Update"
                      handleChange={(e) => {
                        handleWebsiteNext(() => {
                          toggleEdit(e, null);
                        });
                      }}
                    />
                  )}
                </div>

                <EditInput title="URL">
                  <Switch test={editSection === "slug" ? 1 : 0}>
                    <div condition={1}>
                      <SelectWebsiteType
                        websiteType={websiteType}
                        setWebsiteType={setWebsiteType}
                        podcastData={podcastData}
                        handleChange={handleChange}
                      />

                      <AddWebsite
                        websiteType={websiteType}
                        podcastData={podcastData}
                        handleChange={handleChange}
                        slugValidation={slugValidation}
                        validWebsite={validWebsite}
                        setValidWebsite={setValidWebsite}
                      />
                    </div>
                    <p condition={0} className="text-gray-800 sm:px-3 py-2.5">
                      {websiteType === "castos"
                        ? podcastData.podcast_slug + ".castos.com"
                        : podcastData.website}
                    </p>
                  </Switch>
                </EditInput>
              </section>

              <section
                className={`border-b border-gray-200 px-7 py-5 transition-opacity ${
                  editSection !== null &&
                  editSection !== "settings" &&
                  "opacity-50"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-gray-800 md:text-2xl">
                    05. Settings
                  </h2>

                  {editSection !== "settings" && (
                    <EditButton
                      condition={editSection === null}
                      type="Edit"
                      handleChange={(e) => toggleEdit(e, "settings")}
                    />
                  )}

                  {editSection === "settings" && (
                    <EditButton
                      condition={editSection === "settings"}
                      type="Update"
                      handleChange={(e) => {
                        toggleEdit(e, null);
                      }}
                    />
                  )}
                </div>

                <EditInput title="Transcripts">
                  <Switch test={editSection === "settings" ? 1 : 0}>
                    <div condition={1}>
                      <Transcripts
                        podcastData={podcastData}
                        handleChange={handleChange}
                      />
                    </div>
                    <p condition={0} className="text-gray-800 sm:px-3 py-2.5">
                      {podcastData.transcripts_enabled ? "On" : "Off"}
                    </p>
                  </Switch>
                </EditInput>
              </section>

              <section
                className={`border-b border-gray-200 px-7 py-5 transition-opacity ${
                  editSection !== null &&
                  editSection !== "extra" &&
                  "opacity-50"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-gray-800 md:text-2xl">
                    06. Extra <span className="hidden sm:inline">podcast</span>{" "}
                    information
                  </h2>

                  {editSection !== "extra" && (
                    <EditButton
                      condition={editSection === null}
                      type="Edit"
                      handleChange={(e) => toggleEdit(e, "extra")}
                    />
                  )}

                  {editSection === "extra" && (
                    <EditButton
                      condition={editSection === "extra"}
                      type="Update"
                      handleChange={(e) => toggleEdit(e, null)}
                    />
                  )}
                </div>

                <EditInput title="Language">
                  <Switch test={editSection === "extra" ? 1 : 0}>
                    <SelectInput
                      condition={1}
                      handleChange={(e) =>
                        handleChange("language", e.target.value)
                      }
                      selectedCategory={podcastData.language}
                      name="language"
                      title="Language"
                    >
                      {languages.map((language, index) => (
                        <option key={index} value={language.key}>
                          {language.value}
                        </option>
                      ))}
                    </SelectInput>
                    <p condition={0} className="text-gray-800 sm:px-3 py-[9px]">
                      {podcastData.language}
                    </p>
                  </Switch>
                </EditInput>

                <EditInput title="Explicit">
                  <Switch test={editSection === "extra" ? 1 : 0}>
                    <SelectInput
                      condition={1}
                      handleChange={(e) =>
                        handleChange("explicit", e.target.value)
                      }
                      selectedCategory={podcastData.explicit}
                      name="explicit"
                      title="Explicit"
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </SelectInput>
                    <p condition={0} className="text-gray-800 sm:px-3 py-[9px]">
                      {podcastData.explicit === 0 ? "No" : "Yes"}
                    </p>
                  </Switch>
                </EditInput>
              </section>
              <section
                className={`transition-opacity px-7 py-5 ${
                  editSection !== null &&
                  editSection !== "owner" &&
                  "opacity-50"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl text-gray-800 md:text-2xl">
                    07. Owner details
                  </h2>

                  {editSection !== "owner" && (
                    <EditButton
                      condition={editSection === null}
                      type="Edit"
                      handleChange={(e) => toggleEdit(e, "owner")}
                    />
                  )}

                  {editSection === "owner" && (
                    <EditButton
                      condition={
                        editSection === "owner" &&
                        podcastData.podcast_owner &&
                        podcastData.owner_email &&
                        podcastData.author_name &&
                        podcastData.copyright
                      }
                      type="Update"
                      handleChange={(e) => toggleEdit(e, null)}
                    />
                  )}
                </div>

                <EditInput title="Owner Name">
                  <Switch test={editSection === "owner" ? 1 : 0}>
                    <TextInput
                      condition={1}
                      value={podcastData.podcast_owner}
                      name={"podcast_owner"}
                      title={"Owner Name"}
                      handleChange={(e) =>
                        handleChange(
                          "podcast_owner",
                          sanitizeString(e.target.value)
                        )
                      }
                      required={true}
                    />
                    <p condition={0} className="text-gray-800 sm:px-3 py-[9px]">
                      {podcastData.podcast_owner}
                    </p>
                  </Switch>
                </EditInput>

                <EditInput title="Owner Email">
                  <Switch test={editSection === "owner" ? 1 : 0}>
                    <TextInput
                      condition={1}
                      value={podcastData.owner_email}
                      name={"owner_email"}
                      title={"Owner Email"}
                      handleChange={(e) =>
                        handleChange(
                          "owner_email",
                          sanitizeString(e.target.value)
                        )
                      }
                      required={true}
                    />
                    <p condition={0} className="text-gray-800 sm:px-3 py-[9px]">
                      {podcastData.owner_email}
                    </p>
                  </Switch>
                </EditInput>

                <EditInput title="Host Name">
                  <Switch test={editSection === "owner" ? 1 : 0}>
                    <TextInput
                      condition={1}
                      value={podcastData.author_name}
                      name={"author_name"}
                      title={"Host Name"}
                      handleChange={(e) =>
                        handleChange(
                          "author_name",
                          sanitizeString(e.target.value)
                        )
                      }
                      required={true}
                    />
                    <p condition={0} className="text-gray-800 sm:px-3 py-[9px]">
                      {podcastData.author_name}
                    </p>
                  </Switch>
                </EditInput>

                <EditInput title="Copyright">
                  <Switch test={editSection === "owner" ? 1 : 0}>
                    <TextInput
                      condition={1}
                      value={podcastData.copyright}
                      name={"copyright"}
                      title={"copyright"}
                      handleChange={(e) =>
                        handleChange(
                          "copyright",
                          sanitizeString(e.target.value)
                        )
                      }
                      required={true}
                    />
                    <p condition={0} className="text-gray-800 sm:px-3 py-[9px]">
                      {podcastData.copyright}
                    </p>
                  </Switch>
                </EditInput>
              </section>

              <div className="pb-5 px-7">
                {editSection !== null || requestState === "loading" ? (
                  <button
                    disabled
                    className="block w-full py-2 text-base text-purple-600 border border-purple-400 opacity-50 cursor-default btn-solid px-7 sm:px-8 sm:py-3"
                  >
                    Create my podcast
                    {requestState === "loading" && (
                      <Heroicon
                        icon="o-arrow-path"
                        style="w-4 h-4 inline ml-1 animate-spin"
                      />
                    )}
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      createPodcast(e);
                      if (firstPodcast)
                        window.applyTags("create_first_podcast_confirmed");
                    }}
                    className="block w-full py-2 text-base btn-solid btn-primary px-7 sm:px-8 sm:py-3"
                  >
                    Create my podcast
                  </button>
                )}
              </div>
            </>
          )}
        </form>
      )}
      {activeIndex === 7 && (
        <>
          <Confetti />
          <section className="items-center gap-6 p-5 mt-6 bg-white rounded-lg shadow sm:flex">
            <div className="overflow-hidden rounded-xl h-0 pb-[100%] sm:pb-0 sm:h-40 sm:w-40 relative">
              {podcastCover.src ? (
                <img
                  src={podcastCover.src}
                  className="absolute inset-0 block object-cover min-h-full rounded-xl"
                  alt="Podcast Cover"
                />
              ) : (
                <img
                  src={
                    podcastData.default_cover_image
                      ? coverUrl +
                        getDefaultCoverSrc(podcastData.default_cover_image)
                      : "/assets/images/cover-placeholder.png"
                  }
                  className="absolute inset-0 block object-cover min-h-full rounded-xl"
                  alt="Podcast Cover"
                />
              )}
            </div>
            <div className="flex-1 mt-4 sm:mt-0">
              <h2 className="text-xl text-gray-800 md:text-2xl line-clamp-1">
                {podcastData.podcast_title}
              </h2>
              <p className="mt-4 text-gray-700 line-clamp-2">
                {podcastData.podcast_description}
              </p>
              <div className="flex flex-col gap-4 mt-6 lg:flex-row">
                <a
                  href={`/podcasts/${podcastData.podcast_id}/episodes/create`}
                  className="inline-block text-sm btn-solid btn-primary sm:text-base"
                >
                  Add first episode
                </a>
                <a
                  href={`/podcasts/${podcastData.podcast_id}`}
                  className="inline-block text-sm btn-outlined btn-primary sm:text-base"
                >
                  Go to dashboard
                </a>
              </div>
            </div>
          </section>
          <h2 className="mt-10 text-2xl text-gray-800 md:text-3xl md:text-center">
            Quick Actions
          </h2>
          <QuickActions firstPodcast={firstPodcast} podcastData={podcastData} />
        </>
      )}
      <div
        className="absolute left-0 right-0 grid place-items-center bottom-10"
        aria-hidden="true"
      >
        <div className="flex gap-1.5 items-center">
          <div className="w-2 h-2 bg-purple-300 rounded-full"></div>
          <div
            className={`w-2 h-2 ${
              activeIndex >= 1 && activeIndex <= 5
                ? "bg-purple-600"
                : "bg-purple-300"
            } rounded-full`}
          ></div>
          <div
            className={`w-2 h-2 ${
              activeIndex === 6 ? "bg-purple-600" : "bg-purple-300"
            }  rounded-full`}
          ></div>
          <div
            className={`w-2 h-2 ${
              activeIndex === 7 ? "bg-purple-600" : "bg-purple-300"
            }  rounded-full`}
          ></div>
        </div>
      </div>
    </>
  );
}

const initCreatePodcast = function () {
  const elements = document.querySelectorAll(
    "[data-react-component='create-podcast']"
  );
  if (elements.length > 0) {
    elements.forEach((element) => {
      const root = createRoot(element);
      const coverUrl = element.dataset.url;
      const firstPodcast = element.dataset.firstpodcast === "1" ? true : false;
      const fromWizard = element.dataset.fromwizard === "1" ? true : false;
      const transcriptsLimit = Number(element.dataset.transcriptslimit);
      root.render(
        <CreatePodcast
          coverUrl={coverUrl}
          firstPodcast={firstPodcast}
          fromWizard={fromWizard}
          transcriptsLimit={transcriptsLimit}
        />
      );
    });
  }
};

export { CreatePodcast as default, initCreatePodcast };
